import { apiGet, apiPost, apiPatch, apiDelete } from "../../shared/http.js";
import {
  CreateTaxonomyRequestSchema,
  TaxonomyListResponseSchema,
  TaxonomyApiItemSchema,
  type CreateTaxonomyRequest,
  type TaxonomyApiItem,
  type TaxonomyKind,
  type TaxonomyListResponse,
  type PatchTaxonomyRequest,
} from "@recipevault/shared";
import { z } from "zod";

interface ListOptions {
  updatedSince?: string;
  cursor?: string;
  limit?: number;
}

export async function getTaxonomy(kind: TaxonomyKind, opts: ListOptions = {}): Promise<TaxonomyListResponse> {
  const qs = new URLSearchParams({ kind });
  if (opts.updatedSince) qs.set("updatedSince", opts.updatedSince);
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  const raw = await apiGet<unknown>(`/taxonomy?${qs.toString()}`);
  return TaxonomyListResponseSchema.parse(raw);
}

const CreateTaxonomyResponseSchema = z.object({ item: TaxonomyApiItemSchema });
type CreateTaxonomyResponse = z.infer<typeof CreateTaxonomyResponseSchema>;

export async function createTaxonomy(body: CreateTaxonomyRequest, idempotencyKey: string): Promise<CreateTaxonomyResponse> {
  // Validate locally before hitting the network — surfaces a friendly error fast.
  CreateTaxonomyRequestSchema.parse(body);
  const raw = await apiPost<unknown>("/taxonomy", body, { idempotencyKey });
  return CreateTaxonomyResponseSchema.parse(raw);
}

const PatchTaxonomyResponseSchema = z.object({ item: TaxonomyApiItemSchema });
type PatchTaxonomyResponse = z.infer<typeof PatchTaxonomyResponseSchema>;

export async function patchTaxonomy(
  kind: TaxonomyKind,
  id: string,
  body: PatchTaxonomyRequest,
): Promise<PatchTaxonomyResponse> {
  const raw = await apiPatch<unknown>(`/taxonomy/${kind}/${id}`, body);
  return PatchTaxonomyResponseSchema.parse(raw);
}

export async function deleteTaxonomy(kind: TaxonomyKind, id: string): Promise<void> {
  await apiDelete(`/taxonomy/${kind}/${id}`);
}

export type { TaxonomyApiItem };
